import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import clsx from 'clsx';
import { BaseDialog } from '@app/components/BaseDialog/BaseDialog';
import { Button } from '@app/components/Button/Button';
import { ConfirmDialog } from '@app/components/ConfirmDialog/ConfirmDialog';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import EditIcon from '@app/assets/icons/edit-gray.svg?react';
import RefreshIcon from '@app/assets/icons/refresh.svg?react';
import eyeIcon from '@app/assets/icons/eye.png';
import { getBusinessInfo } from '@app/services/business';
import { getEmailPreviewHtml, getMessagePreviewHtml } from '@app/utils/htmlTemplates';
import { MessageTemplateChannel } from '@app/utils/consts';
import { BusinessInterface, EmailDesignContent, MessageDesignContent } from '@app/models';
import { useResolvedDesignTemplate } from './useResolvedDesignTemplate';

type DesignContent = EmailDesignContent | MessageDesignContent;

export const DesignPreviewDialog = ({
  open,
  onClose,
  title,
  channel,
  content,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  channel: MessageTemplateChannel;
  content: DesignContent;
}) => {
  const { data: business } = useQuery({
    queryKey: ['business-info'],
    queryFn: () => getBusinessInfo(),
    select: (response) => response.data as BusinessInterface,
  });

  // The business logo is only a preview fallback, same as in TemplateDesignEditor
  const previewHtml = useMemo(() => {
    if (!open) return '';

    const businessLogo = business?.photo?.url ?? '';
    if (channel === MessageTemplateChannel.EMAIL) {
      const emailContent = content as EmailDesignContent;
      return getEmailPreviewHtml({ ...emailContent, logoUrl: emailContent.logoUrl || businessLogo });
    }

    const messageContent = content as MessageDesignContent;
    return getMessagePreviewHtml({ ...messageContent, logoUrl: messageContent.logoUrl || businessLogo }, business?.name ?? '');
  }, [open, channel, content, business]);

  return (
    <BaseDialog size="max-w-4xl" open={open} onClose={onClose}>
      <div className="border-b border-b-base-ghostWhite p-6 flex gap-2 items-center justify-between">
        <h5 className="font-bold text-lg">{title}</h5>
        <button type="button" onClick={onClose} aria-label="Close preview" className="text-gray-400 hover:text-gray-600">
          <CloseIcon />
        </button>
      </div>
      <div className="p-6 pt-2">
        <iframe srcDoc={previewHtml} title={`${title} preview`} className="w-full h-[60vh] border border-gray-200 rounded-lg bg-white" />
      </div>
    </BaseDialog>
  );
};

type DesignTemplatePickerProps = {
  label: string;
  // Linked template id; `null` means "use whatever template is the business default"
  value: string | null;
  onChange: (templateId: string | null) => void;
  // Removes the custom content, so the linked template is used again
  onReset: () => void;
  error?: string;
  disabled?: boolean;
} & (
  | {
      channel: MessageTemplateChannel.EMAIL;
      // The automation's/campaign's own design for this channel. When set, it's sent instead of the linked template
      customContent?: EmailDesignContent | null;
      onEdit: (content: EmailDesignContent) => void;
    }
  | {
      channel: MessageTemplateChannel.SMS;
      customContent?: MessageDesignContent | null;
      onEdit: (content: MessageDesignContent) => void;
    }
);

// Template select for automations/campaigns: "Business default" (null) or a business template, plus Preview/Edit and a
// "Custom" state. Controlled and storage-free: the parent owns the id and the custom content
export const DesignTemplatePicker = (props: DesignTemplatePickerProps) => {
  const { channel, label, value, onChange, customContent, onReset, error, disabled } = props;
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null | undefined>(undefined);
  const { options, selectedLabel, basedOnName, content, title, isCustom, isResolving, isUnavailable, isListError, refetch } = useResolvedDesignTemplate(channel, value, customContent);
  // Without the template its content is unknown, so preview/edit would show the built-in default instead
  const canUseContent = isCustom || (!isResolving && !isUnavailable);

  const handleSelect = (templateId: string | null) => {
    if (templateId === value) return;

    // The custom content would still win over the newly selected template, so it has to go
    if (isCustom) {
      setPendingTemplateId(templateId);
      return;
    }
    onChange(templateId);
  };

  const confirmSelect = () => {
    if (pendingTemplateId === undefined) return;

    onReset();
    onChange(pendingTemplateId);
    setPendingTemplateId(undefined);
  };

  const handleEdit = () => {
    if (props.channel === MessageTemplateChannel.EMAIL) {
      props.onEdit(content as EmailDesignContent);
    } else {
      props.onEdit(content as MessageDesignContent);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-4 items-end">
        <div className="flex-1 min-w-0">
          <Listbox value={value} onChange={handleSelect} disabled={disabled}>
            <label className="font-medium text-sm inline-block mb-1">{label}</label>
            <div className="relative w-full">
              <ListboxButton
                className={clsx(
                  'bg-base-seasalt rounded py-3 px-4 pr-10 w-full text-left outline-brand-baseBlue outline-1',
                  error && 'border border-system-error',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                <span className="block truncate">{selectedLabel}</span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                </span>
              </ListboxButton>
              <ListboxOptions className="absolute left-0 right-0 z-[9999] w-full mt-1 rounded-lg shadow-lg bg-white max-h-64 overflow-y-auto border border-gray-200">
                {options.map((option) => (
                  <ListboxOption
                    key={option.value ?? 'default'}
                    value={option.value}
                    className="data-[focus]:bg-brand-lightBlue4 data-[selected]:font-medium p-4 text-sm border-b border-b-base-seasalt cursor-pointer"
                  >
                    {option.label}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>
        </div>
        <Button kind="secondary" type="button" onClick={() => setIsPreviewOpen(true)} disabled={!canUseContent} className="!min-w-fit mb-[2px]" leftNode={<img src={eyeIcon} alt="" className="w-4 h-4" />}>
          Preview
        </Button>
        <Button kind="secondary" type="button" onClick={handleEdit} disabled={disabled || !canUseContent} className="!min-w-fit mb-[2px]" leftNode={<EditIcon />}>
          Edit
        </Button>
      </div>

      {isCustom && (
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-brand-lightBlue4 text-brand-baseBlue px-2 py-1 rounded-full font-medium">Custom (based on {basedOnName})</span>
          <button type="button" onClick={onReset} disabled={disabled} className="flex items-center gap-1 text-brand-baseBlue font-medium disabled:opacity-50">
            <RefreshIcon className="w-4 h-4 [&_path]:stroke-brand-baseBlue" /> Reset to template
          </button>
        </div>
      )}

      {isListError && (
        <p role="alert" className="text-system-error text-sm text-left">
          Couldn't load templates, so only the business default can be selected.{' '}
          <button type="button" onClick={() => refetch()} className="text-brand-baseBlue font-medium underline">
            Try again
          </button>
        </p>
      )}

      {error && (
        <p role="alert" className="text-system-error text-sm text-left">
          {error}
        </p>
      )}

      <DesignPreviewDialog open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} title={title} channel={channel} content={content} />

      <ConfirmDialog
        open={pendingTemplateId !== undefined}
        onClose={() => setPendingTemplateId(undefined)}
        onConfirm={confirmSelect}
        title="Discard custom design?"
        message="This channel has a custom design, which is sent instead of the selected template. Choosing another template removes the custom design."
        confirmText="Discard and switch"
        cancelText="Keep custom design"
        variant="warning"
      />
    </div>
  );
};
